import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SESSION_KEY = 'geo_checked_v1';
const OVERRIDE_KEY = 'geo_override';

/**
 * GeoGuard — if the visitor's country is Italy (IT), redirect from any English
 * marketing page to the Italian institutional site at /it. Applies only to
 * public marketing routes; admin and /it itself are untouched.
 *
 * Override: append ?intl=1 to any URL to force-view the English network
 * (useful for demo and for the firm to preview from Italy).
 */
export default function GeoGuard() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Skip guard on /it, /admin and static assets
    if (location.pathname.startsWith('/it') || location.pathname.startsWith('/admin')) return;

    // Domain-based routing: studiobelliardo.it always serves the Italian institutional site
    if (typeof window !== 'undefined' && /studiobelliardo\.it$/i.test(window.location.hostname)) {
      navigate('/it', { replace: true });
      return;
    }

    // Manual override via query string persists in sessionStorage
    const params = new URLSearchParams(location.search);
    if (params.get('intl') === '1') {
      sessionStorage.setItem(OVERRIDE_KEY, '1');
      return;
    }
    if (sessionStorage.getItem(OVERRIDE_KEY) === '1') return;
    if (sessionStorage.getItem(SESSION_KEY) === 'done') return;

    (async () => {
      try {
        let country = '';
        // Primary: Cloudflare edge trace (same-origin, visitor's real country,
        // no external service, no rate limits). Available on any CF-proxied host.
        try {
          const t = await fetch('/cdn-cgi/trace', { cache: 'no-store' });
          if (t.ok) {
            const m = (await t.text()).match(/^loc=([A-Z]{2})$/m);
            if (m) country = m[1];
          }
        } catch { /* fall through to backend endpoint */ }
        // Fallback: backend geo endpoint
        if (!country) {
          const r = await axios.get(`${API}/geo`, { timeout: 5000 });
          country = (r.data?.country || '').toUpperCase();
        }
        sessionStorage.setItem(SESSION_KEY, 'done');
        if (country === 'IT') {
          // Italian visitors land on the Italian institutional site, including
          // the Italian version of the legal pages
          const isLegal = location.pathname === '/privacy' || location.pathname === '/cookie-policy';
          navigate(isLegal ? `/it${location.pathname}` : '/it', { replace: true });
        }
      } catch {
        // fail-open: never block on network errors
        sessionStorage.setItem(SESSION_KEY, 'done');
      }
    })();
  }, [location.pathname, location.search, navigate]);

  return null;
}
